import React from 'react';

import { ResumeContentContext } from '../../../types';

/**
 * Renderiza el contenido inicial solo con state/guarantor (modo broker)
 */
const renderInitialContent = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-initial-content-broker">
      <div className='text-summary'>Show me loan terms for a property {locationPreposition} {steps.state.element}</div>
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con loanType (modo broker)
 */
const renderWithLoanType = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-with-loan-type-broker">
      <div className='text-summary'>Show me {steps.loanType.element} loan terms for a property {locationPreposition} {steps.state.element}</div>
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con loanOwner (modo broker)
 */
const renderWithLoanOwner = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-with-loan-owner-broker">
      Show me {steps.loanType.element} loan terms for a property {steps.loanOwner.element} {locationPreposition} {steps.state.element}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con propertyType (modo broker)
 */
const renderWithPropertyType = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-with-property-type-broker">
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido compacto (una línea) para modo broker
 */
const renderCompact = (
  steps: ResumeContentContext['steps'],
  ficoElementResolved: React.ReactNode,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-compact-broker">
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. The borrower has a FICO score of {ficoElementResolved}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido expandido (multilínea) para modo broker
 */
const renderExpanded = (
  steps: ResumeContentContext['steps'],
  guarantor: ResumeContentContext['guarantor'],
  isSomeoneElse: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  locationPreposition: 'in' | 'at'
) => {
  const propertyTypeElement = steps.propertyType.isSet 
    ? steps.propertyType.element 
    : <span>property</span>;
  
  return (
    <React.Fragment>
      <div className='text-summary' data-testid="render-expanded-broker-summary">
        Show me {steps.loanType.element} loan terms for a {propertyTypeElement} {steps.loanOwner.element} {locationPreposition} {steps.state.element}.
      </div>
      <div className='text-info-borrower' data-testid="render-expanded-broker-info">
        {isSomeoneElse
          ? (
            <>
              {guarantor.text || 'The guarantor'} has a FICO score of {ficoElementResolved} and has purchased/sold {propertiesElementResolved} in the past 36 months
            </>
          )
          : (
            <>
              The borrower has a FICO score of {steps.ficoRange.element} and has purchased/sold {steps.propertiesNegotiated.element} in the past 36 months
            </>
          )}
      </div>
    </React.Fragment>
  );
};

/**
 * Renderiza el contenido completo en una línea para modo broker
 */
const renderCompleteOneLine = (
  steps: ResumeContentContext['steps'],
  isBrokerResume: boolean,
  ficoElementResolved: React.ReactNode,
  propertiesElementResolved: React.ReactNode,
  locationPreposition: 'in' | 'at'
) => {
  if (isBrokerResume) {
    return (
      <React.Fragment>
        <div data-testid="render-complete-one-line-broker-resume">
          My borrower wants a {steps.loanType.element} loan for a {steps.propertyType.element} {steps.loanOwner.element}. They have a FICO score of {ficoElementResolved} and have purchased/sold {propertiesElementResolved} in the last 36 months
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div data-testid="render-complete-one-line-broker">
        Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. The borrower has a FICO score of {ficoElementResolved} and has purchased/sold {propertiesElementResolved} in the past 36 months
      </div>
    </React.Fragment>
  );
};

export const brokerRenderers = {
  renderInitialContent,
  renderWithLoanType,
  renderWithLoanOwner,
  renderWithPropertyType,
  renderCompact,
  renderExpanded,
  renderCompleteOneLine,
};

